import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

const posts = [
  {
    slug: '5-signs-child-ready-music-lessons',
    category: 'For Parents',
    categoryColor: 'bg-brand-yellow/20 text-brand-yellow-dark',
    title: '5 Signs Your Child Is Ready for Music Lessons',
    excerpt: 'Not sure if your child is ready to start music lessons? Here are five clear signs that your child is primed to thrive — and why starting early makes all the difference.',
    readTime: '4 min read',
    date: 'April 2026',
    img: 'https://public.readdy.ai/ai/img_res/0fc9a57b0dc8a1a2eda26b742aa8a63f.jpg',
    featured: true,
    body: [
      { heading: '1. They Show Curiosity About Music', text: 'Does your child hum along to songs, tap rhythms on the table, or ask questions about instruments they see? Curiosity is the number one predictor of success in music lessons. When a child is naturally drawn to sound and rhythm, they\'re already halfway there.' },
      { heading: '2. They Can Focus for 20–30 Minutes', text: 'Music lessons require a child to sit, listen, and practice — even when it\'s challenging. If your child can engage with a book, a puzzle, or a game for 20–30 minutes without losing interest, they\'re developmentally ready for structured instruction.' },
      { heading: '3. They Follow Simple Instructions', text: 'A good music lesson involves back-and-forth communication between student and instructor. Children who can follow two or three-step directions are ready to absorb the kind of guidance that leads to real musical growth.' },
      { heading: '4. They Express Themselves Creatively', text: 'Kids who love to draw, dance, sing, or make up stories are already tapping into the same creative energy that music develops. Creative expression and musical ability go hand in hand — and we nurture both.' },
      { heading: '5. They Ask About It', text: 'This one is simple: if your child has asked to learn an instrument or take singing lessons, listen to them. That self-motivation is gold. Students who want to be there make the fastest progress and have the most fun doing it.' },
      { heading: 'What Age Is Best?', text: 'At Creative Institute, we work with students as young as 4 years old. Our instructors are trained to meet each child where they are developmentally. Whether your child is 4 or 14, there\'s a program designed for them — and we come directly to your home across North Carolina.' },
    ],
  },
  {
    slug: 'benefits-music-education-neurodivergent-children',
    category: 'Neurodivergent',
    categoryColor: 'bg-brand-purple/15 text-brand-purple',
    title: 'How Music Education Benefits Neurodivergent Children',
    excerpt: 'Research shows that music has a profound impact on children with ADHD, autism, dyslexia, and other learning differences. Here\'s what the science says — and what we\'ve seen firsthand.',
    readTime: '5 min read',
    date: 'March 2026',
    img: 'https://readdy.ai/api/search-image?query=child%20with%20headphones%20listening%20to%20music%20with%20eyes%20closed%2C%20peaceful%20expression%2C%20soft%20warm%20indoor%20lighting%2C%20cozy%20home%20environment%2C%20gentle%20and%20calming%20atmosphere%2C%20authentic%20photography&width=800&height=500&seq=blog-neuro-music-001&orientation=landscape',
    featured: false,
    body: [
      { heading: 'Music and the Neurodivergent Brain', text: 'For children with ADHD, autism spectrum disorder, dyslexia, or sensory processing differences, traditional classroom learning can feel overwhelming. Music, however, engages the brain in a completely different way — activating multiple regions simultaneously and creating new neural pathways.' },
      { heading: 'Rhythm Builds Focus', text: 'Studies from Harvard Medical School show that rhythmic training significantly improves attention and impulse control in children with ADHD. Drumming, in particular, has been shown to reduce hyperactivity and increase on-task behavior — without medication.' },
      { heading: 'Music Supports Communication', text: 'For children on the autism spectrum, music can serve as a bridge to communication. Vocal training and instrument play help develop turn-taking, listening skills, and emotional expression in ways that traditional therapy often cannot replicate.' },
      { heading: 'Building Confidence Through Achievement', text: 'One of the most powerful things we see at Creative Institute is the moment a neurodivergent student plays their first full song. That sense of accomplishment — of mastering something real — builds self-esteem that carries into every other area of their life.' },
      { heading: 'Our Approach at Creative Institute', text: 'Our neurodivergent development program is built around patience, flexibility, and genuine care. We adapt every lesson to the student\'s sensory needs, communication style, and learning pace. We come to your home across North Carolina, so your child learns in the environment where they feel safest.' },
    ],
  },
  {
    slug: 'mobile-music-lessons-vs-studio',
    category: 'Mobile Learning',
    categoryColor: 'bg-[#DCFCE7] text-[#166534]',
    title: 'Mobile Music Lessons vs. Studio Lessons: Which Is Better?',
    excerpt: 'More families across North Carolina are choosing mobile music lessons over traditional studio settings. Here\'s an honest comparison — and why the answer might surprise you.',
    readTime: '4 min read',
    date: 'March 2026',
    img: 'https://readdy.ai/api/search-image?query=music%20instructor%20teaching%20a%20teenager%20guitar%20in%20a%20bright%20cozy%20living%20room%2C%20warm%20afternoon%20light%20through%20windows%2C%20relaxed%20home%20learning%20environment%2C%20authentic%20candid%20moment%2C%20natural%20photography&width=800&height=500&seq=blog-mobile-lessons-001&orientation=landscape',
    featured: false,
    body: [
      { heading: 'The Traditional Studio Model', text: 'For decades, music lessons meant driving to a studio, waiting in a lobby, and squeezing into a 30-minute slot between other students. While studios have their place, this model creates friction — especially for busy families across North Carolina juggling school, work, and activities.' },
      { heading: 'What Mobile Lessons Change', text: 'When a Creative Institute instructor comes to your home, everything shifts. Your child is in their comfort zone. There\'s no commute stress. The lesson can start on time, run the full duration, and end without rushing to the car. Parents can observe without feeling like they\'re in the way.' },
      { heading: 'The Research on Home Learning', text: 'Educational research consistently shows that students learn faster and retain more when they\'re in familiar, low-stress environments. The home removes the performance anxiety that many students feel in a studio setting — especially beginners.' },
      { heading: 'Cost Comparison', text: 'When you factor in gas, time, and the hidden cost of missed lessons due to scheduling conflicts, mobile lessons often come out ahead financially. At Creative Institute, we work with your budget and offer flexible payment options — call us to discuss what works for your family.' },
      { heading: 'Who Mobile Lessons Are Perfect For', text: 'Mobile lessons are ideal for young children, neurodivergent students, busy families, adults returning to music, and anyone who values convenience without sacrificing quality. We serve families across Raleigh, Charlotte, Fayetteville, Durham, Greensboro, and dozens of other NC cities.' },
    ],
  },
  {
    slug: 'how-to-practice-music-at-home',
    category: 'Tips & Advice',
    categoryColor: 'bg-[#FEF9C3] text-[#854D0E]',
    title: 'How to Practice Music at Home (Without the Battles)',
    excerpt: 'Getting kids to practice is one of the biggest challenges parents face. Here are proven strategies that actually work — and make practice something your child looks forward to.',
    readTime: '5 min read',
    date: 'February 2026',
    img: 'https://readdy.ai/api/search-image?query=child%20practicing%20piano%20at%20home%20with%20parent%20watching%20and%20smiling%2C%20warm%20cozy%20living%20room%2C%20afternoon%20sunlight%2C%20encouraging%20family%20moment%2C%20authentic%20candid%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=blog-practice-home-001&orientation=landscape',
    featured: false,
    body: [
      { heading: 'Why Kids Resist Practice', text: 'Practice resistance is normal — and it\'s not a sign that your child doesn\'t love music. It usually means they\'re tired, overwhelmed, or haven\'t yet connected the dots between practice and progress. The good news: this is fixable.' },
      { heading: 'Keep It Short and Consistent', text: 'For young students, 10–15 minutes of focused daily practice beats a 45-minute session once a week. Consistency builds habit. Set a specific time each day — right after school, before dinner, or after homework — and stick to it.' },
      { heading: 'Make It a Positive Experience', text: 'Never use practice as punishment. Never end a session on a frustrating note. Always find something to celebrate — even if it\'s just one measure played correctly. Positive reinforcement is the engine of musical growth.' },
      { heading: 'Let Them Choose Sometimes', text: 'Give your child some control. Let them pick which song to work on first, or let them play something just for fun at the end of practice. Autonomy increases motivation — and motivation is everything.' },
      { heading: 'Communicate With Your Instructor', text: 'At Creative Institute, our instructors give specific, actionable practice assignments after every session. If your child is struggling with practice, tell us — we\'ll adjust the approach. We\'re partners in your child\'s growth, not just weekly visitors.' },
    ],
  },
  {
    slug: 'music-lessons-adults-north-carolina',
    category: 'Adult Learning',
    categoryColor: 'bg-[#EEF2FF] text-brand-blue-dark',
    title: 'It\'s Never Too Late: Music Lessons for Adults in NC',
    excerpt: 'Always wanted to learn piano or guitar but thought you missed your window? You didn\'t. Here\'s why adult learners often make the best students — and how to get started.',
    readTime: '4 min read',
    date: 'February 2026',
    img: 'https://readdy.ai/api/search-image?query=adult%20woman%20in%20her%2040s%20playing%20piano%20with%20focused%20expression%2C%20warm%20home%20environment%2C%20evening%20lighting%2C%20peaceful%20and%20accomplished%20mood%2C%20authentic%20photography%2C%20soft%20warm%20tones&width=800&height=500&seq=blog-adult-piano-001&orientation=landscape',
    featured: false,
    body: [
      { heading: 'The Myth of the "Too Late" Window', text: 'The idea that you have to start music as a child to ever be good is simply not true. Adult brains are highly capable of learning new skills — and in many ways, adult learners have significant advantages over children.' },
      { heading: 'Why Adults Learn Faster', text: 'Adults bring motivation, discipline, and life experience to the lesson. You understand why you\'re practicing. You can self-correct. You can communicate clearly with your instructor about what\'s working and what isn\'t. These are massive advantages.' },
      { heading: 'What Adults Typically Want to Learn', text: 'Most adult students at Creative Institute want to learn piano, vocals, or guitar. Many want to play songs they love — not scales and theory. We meet you where you are and build a curriculum around your actual goals.' },
      { heading: 'Fitting Lessons Into a Busy Life', text: 'This is where mobile lessons shine for adults. We come to you — evenings and weekends available. No commute, no scheduling gymnastics. Just a professional instructor at your door, ready to help you finally learn that instrument you\'ve always wanted to play.' },
      { heading: 'Getting Started in North Carolina', text: 'Creative Institute serves adult learners across Raleigh, Charlotte, Fayetteville, Durham, Greensboro, Wilmington, and many more NC cities. Call us for a free consultation — we\'ll match you with the right instructor and program for your goals.' },
    ],
  },
  {
    slug: 'ministry-music-staffing-nc-churches',
    category: 'Ministry',
    categoryColor: 'bg-brand-yellow/20 text-brand-yellow-dark',
    title: 'How NC Churches Are Finding Music Directors Through Creative Institute',
    excerpt: 'Finding qualified worship musicians and music directors is one of the biggest challenges for churches across North Carolina. Here\'s how our free Ministry Connections program is changing that.',
    readTime: '3 min read',
    date: 'January 2026',
    img: 'https://readdy.ai/api/search-image?query=church%20choir%20singing%20together%20with%20hands%20raised%20in%20worship%2C%20warm%20golden%20light%2C%20beautiful%20sanctuary%20interior%2C%20diverse%20congregation%2C%20uplifting%20spiritual%20atmosphere%2C%20authentic%20photography&width=800&height=500&seq=blog-ministry-001&orientation=landscape',
    featured: false,
    body: [
      { heading: 'The Worship Staffing Gap', text: 'Across North Carolina, hundreds of churches struggle to find qualified musicians, worship leaders, and creative directors. The talent exists — but connecting churches with the right people has always been a challenge.' },
      { heading: 'What Ministry Connections Does', text: 'Creative Institute\'s Ministry Connections program bridges that gap — completely free of charge. We connect churches and faith organizations with vetted, talented musicians and creative professionals who are looking for ministry opportunities.' },
      { heading: 'Who We Connect', text: 'We work with pianists, vocalists, drummers, worship leaders, sound engineers, and creative directors. Whether you need someone for a Sunday service, a special event, or a full-time position, we can help.' },
      { heading: 'Why It\'s Free', text: 'Ministry Connections is our way of giving back to the faith community. We believe that every church — regardless of size or budget — deserves access to talented, passionate worship musicians. There is no fee for churches or for the creatives we place.' },
      { heading: 'How to Get Connected', text: 'If you\'re a church leader in North Carolina looking for worship musicians, or a creative professional looking for ministry opportunities, reach out through our Ministry Connections page. We\'ll handle the rest.' },
    ],
  },
];

const blogJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'Creative Institute Blog',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog`,
  description: 'Tips, guides, and insights for parents, students, and creatives across North Carolina — from music education tips to program guides.',
  publisher: {
    '@type': 'EducationalOrganization',
    name: 'Creative Institute LLC',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`,
  },
  blogPost: [
    { '@type': 'BlogPosting', headline: '5 Signs Your Child Is Ready for Music Lessons', datePublished: '2026-04-01', author: { '@type': 'Organization', name: 'Creative Institute LLC' } },
    { '@type': 'BlogPosting', headline: 'How Music Education Benefits Neurodivergent Children', datePublished: '2026-03-15', author: { '@type': 'Organization', name: 'Creative Institute LLC' } },
    { '@type': 'BlogPosting', headline: 'Mobile Music Lessons vs. Studio Lessons: Which Is Better?', datePublished: '2026-03-01', author: { '@type': 'Organization', name: 'Creative Institute LLC' } },
  ],
};

function FeaturedPost({ post }: { post: typeof posts[0] }) {
  const [expanded, setExpanded] = useState(false);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div ref={ref} className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} rounded-3xl overflow-hidden border border-gray-100 bg-white card-lift`}>
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative h-64 lg:h-auto min-h-[320px]">
          <img src={post.img} alt={post.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          <span className={`absolute top-5 left-5 px-3 py-1 rounded-full font-body text-xs font-semibold ${post.categoryColor}`}>{post.category}</span>
        </div>
        <div className="p-8 lg:p-12 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-body text-gray-400 text-xs">{post.date}</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span className="font-body text-gray-400 text-xs">{post.readTime}</span>
            <span className="inline-block px-2.5 py-0.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-black text-[10px] uppercase tracking-wide">Featured</span>
          </div>
          <h2 className="font-heading text-brand-blue-dark text-2xl lg:text-3xl font-black mb-4 leading-tight">{post.title}</h2>
          <p className="font-body text-gray-500 text-sm leading-relaxed mb-6">{post.excerpt}</p>

          {expanded && (
            <div className="mb-6 space-y-5 border-t border-gray-100 pt-6">
              {post.body.map((section) => (
                <div key={section.heading}>
                  <h3 className="font-heading text-brand-blue-dark text-base font-black mb-2">{section.heading}</h3>
                  <p className="font-body text-gray-500 text-sm leading-relaxed">{section.text}</p>
                </div>
              ))}
              <div className="pt-4 border-t border-gray-100">
                <p className="font-body text-gray-400 text-xs italic">Creative Institute serves families across North Carolina with mobile music lessons and creative development programs. <Link to="/contact" className="text-brand-purple hover:underline cursor-pointer">Contact us</Link> to get started.</p>
              </div>
            </div>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity self-start"
          >
            {expanded ? <><i className="ri-arrow-up-line" /> Read Less</> : <><i className="ri-book-open-line" /> Read Full Article</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function BlogCard({ post, delay }: { post: typeof posts[0]; delay: number }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="rounded-2xl overflow-hidden border border-gray-100 bg-white card-lift flex flex-col"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="relative h-48 flex-shrink-0">
        <img src={post.img} alt={post.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        <span className={`absolute top-4 left-4 px-3 py-1 rounded-full font-body text-xs font-semibold ${post.categoryColor}`}>{post.category}</span>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-body text-gray-400 text-xs">{post.date}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span className="font-body text-gray-400 text-xs">{post.readTime}</span>
        </div>
        <h3 className="font-heading text-brand-blue-dark text-lg font-black mb-3 leading-snug">{post.title}</h3>
        <p className="font-body text-gray-500 text-sm leading-relaxed mb-4 flex-1">{post.excerpt}</p>

        {expanded && (
          <div className="mb-5 space-y-4 border-t border-gray-100 pt-5">
            {post.body.map((section) => (
              <div key={section.heading}>
                <h4 className="font-heading text-brand-blue-dark text-sm font-black mb-1.5">{section.heading}</h4>
                <p className="font-body text-gray-500 text-xs leading-relaxed">{section.text}</p>
              </div>
            ))}
            <div className="pt-3 border-t border-gray-100">
              <p className="font-body text-gray-400 text-xs italic">Creative Institute serves families across North Carolina. <Link to="/contact" className="text-brand-purple hover:underline cursor-pointer">Contact us</Link> to get started.</p>
            </div>
          </div>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          className="whitespace-nowrap inline-flex items-center gap-2 font-body text-brand-purple font-semibold text-sm cursor-pointer hover:text-brand-blue-dark transition-colors self-start"
        >
          {expanded ? <><i className="ri-arrow-up-line text-xs" /> Read Less</> : <><i className="ri-arrow-right-line text-xs" /> Read More</>}
        </button>
      </div>
    </div>
  );
}

function PageHero() {
  return (
    <section className="relative min-h-[50vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#1E3A8A] via-[#2D1B69] to-[#5B21B6]" />
      <div className="absolute inset-0 z-0 opacity-10">
        <img src="https://readdy.ai/api/search-image?query=open%20book%20with%20musical%20notes%20floating%20out%2C%20warm%20golden%20light%2C%20creative%20artistic%20concept%2C%20elegant%20background%2C%20no%20text%2C%20wide%20cinematic%20composition&width=1920&height=700&seq=blog-hero-bg-001&orientation=landscape" alt="" className="w-full h-full object-cover object-top" />
      </div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-20 pt-36">
        <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          Tips, Guides &amp; Insights
        </span>
        <h1 className="font-heading text-white text-5xl lg:text-7xl font-black mb-4 leading-tight max-w-3xl">
          The Creative<br /><span className="text-brand-yellow">Institute Blog</span>
        </h1>
        <p className="font-body text-white/75 text-xl max-w-2xl leading-relaxed">
          Practical advice for parents, students, and creatives across North Carolina — from music education tips to program guides.
        </p>
      </div>
    </section>
  );
}

function NewsletterStrip() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch('https://readdy.ai/api/form/d73f22suf8iffoai1qig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  return (
    <section className="py-14 bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="font-heading text-white text-2xl font-black mb-1">Get New Articles in Your Inbox</h3>
            <p className="font-body text-white/65 text-sm">Tips for parents, students, and creatives — delivered free.</p>
          </div>
          {status === 'success' ? (
            <div className="flex items-center gap-3 px-6 py-3 rounded-full bg-brand-yellow/20 border border-brand-yellow/40">
              <i className="ri-check-line text-brand-yellow" />
              <span className="font-body text-white font-semibold text-sm">You&apos;re subscribed!</span>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full sm:w-72 px-5 py-3 rounded-full font-body text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-yellow"
              />
              <button type="submit" className="whitespace-nowrap px-6 py-3 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-all">
                Subscribe Free
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default function BlogPage() {
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.05 });
  const featured = posts.find((p) => p.featured)!;
  const rest = posts.filter((p) => !p.featured);

  return (
    <main>
      <SEOHead
        title="Blog | Creative Institute LLC — Music Education Tips & Guides NC"
        description="Practical advice for parents, students, and creatives across North Carolina — from music education tips to program guides. Mobile music lessons, neurodivergent development, and more."
        keywords="music education blog NC, music lessons tips, neurodivergent music education, mobile music lessons North Carolina, Creative Institute blog"
        canonical="/blog"
        jsonLd={blogJsonLd}
      />
      <PageHero />
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-8">
          <div className="mb-6">
            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30">Featured Article</span>
          </div>
          <FeaturedPost post={featured} />

          <div className="mt-16 mb-8">
            <h2 className="font-heading text-brand-blue-dark text-3xl font-black">More Articles</h2>
          </div>
          <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post, i) => (
              <div key={post.slug} className={`scroll-hidden ${gridVis ? 'scroll-visible' : ''}`} style={{ transitionDelay: `${i * 80}ms` }}>
                <BlogCard post={post} delay={i * 80} />
              </div>
            ))}
          </div>
        </div>
      </section>
      <NewsletterStrip />
    </main>
  );
}
